/**
 * Collapsed day-card summary — one group per ORDERABLE mealtime (a mealtime with
 * an active menu that day; days with nothing orderable show a muted hint), each
 * with the mealtime label + grade badges (abbreviated label + qty) and a tooltip
 * with the full label / mealtime / count. Shared by both order layouts; both
 * pass `green`, so badges tint green where there are orders (qty > 0) and stay
 * gray at zero. The `green` prop is retained for callers that want all-neutral.
 *
 * Mealtime labels are spelled out on sparse rows and abbreviated (B/AS/L/…) only
 * once the row is dense (> 6 total grade pills); see `shouldAbbreviateMealtimes`.
 */

import { cn } from '@ordo/ui/cn'
import { Skeleton } from '@ordo/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ordo/ui/tooltip'
import { Fragment, type ReactElement } from 'react'

import { useLocationActiveMenusQuery } from '@/api/orders'
import { type AdminSchool, serviceDateIso } from '@/api/school-orders'
import { orderableMenusFor } from '@/lib/school-orders/active-menus'
import type { AlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { buildMenuOrderColumns, WHOLE_SCHOOL_COLUMN_ID } from '@/lib/school-orders/columns'
import { abbreviateGrade, shouldAbbreviateMealtimes, shouldStackMealtimes } from '@/lib/school-orders/layout'
import { MEALTIME_ABBR, MEALTIME_LABELS } from '@/lib/school-orders/mealtime'

export interface CollapsedSummaryProps {
  school: AdminSchool
  dateKey: string
  alreadyOrdered: AlreadyOrdered
  /** Force each mealtime onto its own row (defaults on for dense column sets). */
  stack?: boolean
  /** Tint badges green where they have orders (Layout B). */
  green?: boolean
  /**
   * The school marked the day NO_SERVICE (PROD-1228). Replaces the muted
   * "No menu this day" hint, which reads as an authoring gap — the distinction
   * PROD-970 asked for: a holiday is a deliberate closure, not a missing menu.
   */
  noService?: boolean
}

export function CollapsedSummary({
  school,
  dateKey,
  alreadyOrdered,
  stack = false,
  green = false,
  noService = false,
}: CollapsedSummaryProps): ReactElement {
  // Orderable mealtimes come from the day's active menus — the same source the
  // expanded panel uses for its tabs — not the static app-wide mealtime list.
  // `withAllergens: true` matches DayOrderPanel / WeekOrdersView so all three
  // observers of this (location, day) share one cache entry — the summary
  // itself doesn't render tags.
  const { data, isPending } = useLocationActiveMenusQuery({
    locationId: school.id,
    activeOn: serviceDateIso(dateKey),
    withAllergens: true,
  })
  const { mealtimes, menuGradeRangeCodeSetsByMealtime } = orderableMenusFor(data)
  // Columns mirror the expanded panel: the program-keyed locked partition
  // per MEALTIME (AGE_GROUP campuses), campus vocabulary otherwise.
  const columnsFor = (mealtime: (typeof mealtimes)[number]) =>
    buildMenuOrderColumns(school, mealtime, menuGradeRangeCodeSetsByMealtime.get(mealtime) ?? [])

  if (isPending) {
    return <Skeleton className="h-4 w-2/3" />
  }
  // A closure outranks the menu read: the school being shut is the reason
  // nothing can be ordered, whether or not a menu happens to span the day.
  if (noService) {
    return <p className="text-[11px] leading-none text-muted-foreground">No service — school closed</p>
  }
  if (mealtimes.length === 0) {
    return <p className="text-[11px] leading-none text-muted-foreground">No menu this day</p>
  }

  const groups = mealtimes.map((mealtime) => ({
    mealtime,
    chips: columnsFor(mealtime).map((col) => ({
      column: col,
      qty: alreadyOrdered.mealtimeGradeQty(dateKey, mealtime, col.audienceId),
    })),
  }))
  // Spell mealtimes out unless the row is dense (> 6 total grade pills);
  // dense column sets also stack one mealtime per row. Computed here now
  // that the columns are day-derived (the caller no longer knows them).
  const maxColumns = Math.max(0, ...groups.map((group) => group.chips.length))
  const abbreviate = shouldAbbreviateMealtimes(mealtimes.length, maxColumns)
  const stackLayout = stack || shouldStackMealtimes(maxColumns)

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={cn(
          'text-[11px] leading-none',
          stackLayout ? 'flex flex-col gap-y-2.5' : 'flex flex-wrap items-center gap-x-2.5 gap-y-2',
        )}>
        {groups.map((group, i) => (
          <Fragment key={group.mealtime}>
            {!stackLayout && i > 0 ? <span aria-hidden className="h-3.5 w-px shrink-0 bg-border" /> : null}
            <div className="flex flex-wrap items-center gap-1.5">
              {/* Mobile has no hover tooltips, so spell the mealtime out in
                  full; desktop uses the abbreviation only on dense rows. */}
              <span className="font-normal text-foreground">
                <span className="lg:hidden">{MEALTIME_LABELS[group.mealtime]}</span>
                <span className="hidden lg:inline">
                  {abbreviate ? MEALTIME_ABBR[group.mealtime] : MEALTIME_LABELS[group.mealtime]}
                </span>
              </span>
              {group.chips.map(({ column, qty }) => {
                const ordered = green && qty > 0
                return (
                  <Tooltip key={column.columnId}>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          'inline-flex cursor-default items-center gap-1 rounded-full px-2 py-1',
                          ordered
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : 'bg-muted',
                        )}>
                        {/* Whole-school has one column — "Whol." next to every
                            number was noise (Sheila, 2026-08-24). The tooltip
                            still names it. */}
                        {column.columnId === WHOLE_SCHOOL_COLUMN_ID ? null : (
                          <span className={cn(!ordered && 'text-muted-foreground')}>
                            <span className="lg:hidden">{column.label}</span>
                            <span className="hidden lg:inline">{abbreviateGrade(column.label)}</span>
                          </span>
                        )}
                        <span className={cn('font-semibold tabular-nums', !ordered && 'text-foreground')}>{qty}</span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {column.label} {MEALTIME_LABELS[group.mealtime]} · {qty} ordered
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
          </Fragment>
        ))}
      </div>
    </TooltipProvider>
  )
}
